import ctl from '@netlify/classnames-template-literals'
import { MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { ConformedBranding } from '../../../views'
import { extractBrandSetName } from './extract-brand-set-name'

export type SmallBrandingCardProps = {
	branding: ConformedBranding
	selected?: boolean
	onSelect?: () => void
	onDelete?: (brandingId: string) => void
	isDeleting?: boolean
}

export function SmallBrandingCard(
	{ branding, selected = false, onSelect, onDelete, isDeleting = false }: SmallBrandingCardProps,
) {
	const { t } = useTranslation('common')

	const colors = [
		branding.background,
		branding.foreground,
		branding.accentBackground,
		branding.accentForeground,
	].filter((color): color is string => typeof color === 'string' && color.trim().length > 0)

	const displayName = extractBrandSetName(branding.name, branding.website)

	const logoBackgroundImage = branding.logo?.url ? `url('${branding.logo.url}')` : undefined

	const handleDeleteClick = (e: MouseEvent) => {
		e.preventDefault()
		e.stopPropagation()
		onDelete?.(branding.id)
	}

	return (
		<div className={ctl(`relative group w-full min-w-0 ${isDeleting ? 'opacity-50 pointer-events-none' : ''}`)}>
			<button
				className={ctl(`
					shadow-sm text-sm rounded pl-2 pr-4 py-2 group flex items-center justify-between gap-3 bg-white w-full overflow-hidden
					cursor-pointer
					${selected ? 'ring-2 ring-purple-500' : ''}
				`)}
				onClick={onSelect}
				disabled={isDeleting}
			>
				<div className="flex items-center gap-3 min-w-0">
					<span
						className="h-6 w-6 shrink-0 bg-center bg-contain bg-no-repeat rounded"
						style={{ backgroundImage: logoBackgroundImage }}
					/>
					<span className="truncate">{displayName}</span>
				</div>
				{colors.length > 0 && (
					<div className="flex items-center gap-1">
						{colors.map((color, index) => (
							<span
								key={`${branding.id}-c-${index}`}
								className="h-3 w-3 rounded-sm ring ring-grey-100/60"
								style={{ backgroundColor: color }}
							/>
						))}
					</div>
				)}
			</button>
			{onDelete && (
				<button
					type="button"
					onClick={handleDeleteClick}
					disabled={isDeleting}
					className={ctl(`
						absolute top-1/2 -translate-y-1/2 right-1
						flex items-center justify-center
						w-6 h-6 rounded-md
						bg-white/70 hover:bg-red-50 hover:text-red-600
						shadow-sm hover:shadow-md
						text-black/60
						transition-all
						opacity-0 group-hover:opacity-100 focus:opacity-100
						cursor-pointer
						appearance-none border-none
					`)}
					title={t('branding.deleteTooltip')}
					aria-label={t('branding.deleteTooltip')}
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width={14}
						height={14}
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth={2}
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<polyline points="3 6 5 6 21 6" />
						<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
					</svg>
				</button>
			)}
		</div>
	)
}
